/**
 * Chunk Load Error Handler
 * 
 * Handles dynamic import failures (chunk loading errors) that occur when:
 * - Old assets are deleted during deployment
 * - User has stale app version loaded
 * - Network issues prevent chunk loading
 * 
 * Strategy:
 * 1. Detect chunk load errors
 * 2. Force reload to get fresh assets
 * 3. Prevent infinite reload loops
 */

const RELOAD_KEY = 'chunk-load-error-reload';
const MAX_RELOAD_ATTEMPTS = 3;
const RELOAD_TIMEOUT_MS = 10000; // 10 seconds

interface ReloadState {
  count: number;
  timestamp: number;
}

/**
 * Check if an error is a chunk loading error
 */
function isChunkLoadError(error: Error): boolean {
  const message = error.message.toLowerCase();
  
  return (
    // Vite chunk load errors
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('unable to preload css') ||
    message.includes('failed to fetch') ||
    // Webpack chunk load errors (if migrating from webpack)
    message.includes('loading chunk') ||
    message.includes('chunkloaderror') ||
    // Network errors during import
    (error.name === 'TypeError' && message.includes('import'))
  );
}

/**
 * Get current reload state from sessionStorage
 */
function getReloadState(): ReloadState {
  try {
    const stored = sessionStorage.getItem(RELOAD_KEY);
    if (!stored) {
      return { count: 0, timestamp: 0 };
    }
    
    const state: ReloadState = JSON.parse(stored);
    
    // Reset if last reload was more than timeout ago
    if (Date.now() - state.timestamp > RELOAD_TIMEOUT_MS) {
      return { count: 0, timestamp: 0 };
    }
    
    return state;
  } catch {
    return { count: 0, timestamp: 0 };
  }
}

/**
 * Update reload state in sessionStorage
 */
function updateReloadState(state: ReloadState): void {
  try {
    sessionStorage.setItem(RELOAD_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('[CHUNK-ERROR] Failed to update reload state:', error);
  }
}

/**
 * Clear reload state
 */
function clearReloadState(): void {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch (error) {
    console.error('[CHUNK-ERROR] Failed to clear reload state:', error);
  }
}

/**
 * Handle chunk load error with automatic reload
 */
export function handleChunkLoadError(error: Error): boolean {
  if (!isChunkLoadError(error)) {
    return false;
  }
  
  console.error('[CHUNK-ERROR] Chunk loading failed:', error.message);
  
  const state = getReloadState();
  
  // Check if we've exceeded max reload attempts
  if (state.count >= MAX_RELOAD_ATTEMPTS) {
    console.error(
      '[CHUNK-ERROR] Max reload attempts reached. Please manually refresh the page.',
      `Attempts: ${state.count}`
    );
    
    // Clear state so user can try again later
    clearReloadState();
    
    // Show user-friendly error message
    alert(
      'Unable to load the application. This may be due to a new version being deployed.\n\n' +
      'Please refresh the page manually. If the problem persists, try clearing your browser cache.'
    );
    
    return true;
  }
  
  // Increment reload count and update timestamp
  const newState: ReloadState = {
    count: state.count + 1,
    timestamp: Date.now()
  };
  
  updateReloadState(newState);
  
  console.log(
    `[CHUNK-ERROR] Reloading page to fetch fresh assets (attempt ${newState.count}/${MAX_RELOAD_ATTEMPTS})...`
  );
  
  // Force reload with cache bypass
  window.location.reload();
  
  return true;
}

/**
 * Initialize chunk load error handler
 * Sets up global error listeners
 */
export function initChunkLoadErrorHandler(): void {
  // Clear reload state on successful load
  clearReloadState();
  
  // Handle unhandled promise rejections (dynamic imports)
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason instanceof Error) {
      const handled = handleChunkLoadError(event.reason);
      if (handled) {
        event.preventDefault(); // Prevent default error logging
      }
    }
  });
  
  // Handle regular errors
  window.addEventListener('error', (event) => {
    if (event.error instanceof Error) {
      handleChunkLoadError(event.error);
    }
  });
  
  console.log('[CHUNK-ERROR] Chunk load error handler initialized');
}
